<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  chat: { type: Object, required: true },
});

const store = useStore();
const currentUser = computed(() => store.getters.getCurrentUser);
const currentRole = computed(() => store.getters.getCurrentRole);
const customRoleId = computed(() => store.getters.getCurrentCustomRoleId);
const customRoles = computed(() => store.getters['customRole/getCustomRoles'] || []);
const customRoleName = computed(() => {
  if (!customRoleId.value) return null;
  const role = customRoles.value.find(r => r.id === customRoleId.value);
  return role?.name || null;
});
const isSupervisor = computed(() => currentRole.value === 'agent' && customRoleName.value === 'Supervisor');
const isVendor = computed(() => currentRole.value === 'agent' && !isSupervisor.value);
const isAdmin = computed(() => currentRole.value === 'administrator');

onMounted(() => { store.dispatch('customRole/getCustomRole'); });

const hasAssignee = computed(() => !!props.chat?.meta?.assignee);
const isMine = computed(() => {
  const assignee = props.chat?.meta?.assignee;
  return assignee && assignee.id === currentUser.value?.id;
});

const status = computed(() => props.chat?.status);

// Mostrar botões baseado no estado
const showAccept = computed(() => {
  if (status.value === 'resolved') return false;
  if (status.value === 'open') return false; // Abertas: sem aceitar
  if (isMine.value) return false;
  return status.value === 'pending'; // Só em pendentes
});

const showResolve = computed(() => {
  if (status.value === 'resolved') return false;
  if (status.value === 'open') return false; // Abertas: sem finalizar
  if (isVendor.value) return false; // Vendedor: nunca na lista
  return status.value === 'pending'; // Só em pendentes para admin/supervisor
});

const showClosed = computed(() => status.value === 'resolved');

// Ações
const acceptLead = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  const contactName = props.chat?.meta?.sender?.name || `#${props.chat.id}`;
  try {
    // Verificar se já foi aceito
    const conv = await store.dispatch('getConversation', props.chat.id);
    if (conv?.meta?.assignee && conv.meta.assignee.id !== currentUser.value.id) {
      useAlert(`Lead "${contactName}" já foi aceito por ${conv.meta.assignee.name}`);
      return;
    }
    await store.dispatch('assignAgent', {
      conversationId: props.chat.id,
      agentId: currentUser.value.id,
    });
    // Mudar status para open ao aceitar
    if (props.chat.status === 'pending') {
      await store.dispatch('toggleStatus', {
        conversationId: props.chat.id,
        status: 'open',
      });
    }
    useAlert(`Lead "${contactName}" aceito!`);
  } catch {
    useAlert(`Erro ao aceitar "${contactName}"`);
  }
};

const resolveLead = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (!confirm('Finalizar este lead?')) return;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: props.chat.id,
      status: 'resolved',
    });
    useAlert('Lead finalizado');
  } catch {
    useAlert('Erro ao finalizar');
  }
};
</script>

<template>
  <div
    v-if="showAccept || showResolve || showClosed"
    class="absolute right-1 top-2 flex flex-col items-end gap-1 z-20"
    @click.stop
  >
    <!-- ACEITAR (verde, em cima) -->
    <button
      v-if="showAccept"
      style="background-color: #059669; color: white; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; border: none; cursor: pointer;"
      @click="acceptLead"
    >
      ACEITAR
    </button>

    <!-- FINALIZAR (vermelho, embaixo) -->
    <button
      v-if="showResolve"
      style="background-color: #dc2626; color: white; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; border: none; cursor: pointer;"
      @click="resolveLead"
    >
      FINALIZAR
    </button>

    <!-- FECHADO (badge) -->
    <span
      v-if="showClosed"
      class="px-2 py-0.5 rounded text-[9px] font-bold text-white bg-gray-500 cursor-default"
    >
      FECHADO
    </span>
  </div>
</template>
